import { AlarmClockOff, BarChart3, GitBranch, Target, TriangleAlert, Zap } from "lucide-react";
import { Suspense, lazy } from "react";
import { defineFeature } from "../types";

/**
 * Leads — the sales pipeline, from first touch to won (roadmap item 1).
 *
 * `/leads` is the board and the list, `/leads/reports` is US-1.8, `/leads/:id`
 * is one opportunity, and the pipeline configuration of US-1.7 registers itself
 * as a settings section so the settings shell picks it up with no coupling.
 *
 * Every page here is lazy: this module costs the entry bundle its icons and its
 * metadata, nothing more.
 */

const PipelineSettingsPage = lazy(() => import("./settings"));

/**
 * Deliberately plain markup rather than <Skeleton>: this module is imported
 * statically by the registry, so anything it touches lands in the entry chunk.
 * A fallback that flashes for 80ms is not worth a component import there.
 */
function SettingsFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading pipeline settings">
      <div className="skeleton-shimmer h-6 w-56 rounded-item" />
      <div className="skeleton-shimmer h-64 w-full rounded-card" />
      <div className="skeleton-shimmer h-40 w-full rounded-card" />
    </div>
  );
}

export default defineFeature({
  key: "leads",
  title: "Leads",
  entities: ["lead"],
  routes: [
    { path: "leads", lazy: () => import("./page") },
    { path: "leads/reports", lazy: () => import("./reports") },
    { path: "leads/:id", lazy: () => import("./detail") },
  ],
  nav: [
    {
      label: "Leads",
      to: "/leads",
      icon: Target,
      group: "sales",
      order: 10,
      permission: "leads:read",
      description: "The pipeline, from first touch to won",
    },
  ],
  quickCreate: [
    {
      key: "leads",
      label: "Lead",
      icon: Zap,
      to: "/leads?new=1",
      permission: "leads:write",
      order: 10,
      shortcut: "n",
      description: "Capture an enquiry in 20 seconds",
    },
  ],
  commands: [
    {
      key: "leads.board",
      label: "Open the pipeline board",
      icon: Target,
      to: "/leads",
      group: "navigate",
      permission: "leads:read",
      keywords: ["kanban", "pipeline", "deals", "opportunities", "funnel"],
    },
    {
      key: "leads.no-next-step",
      label: "Leads with no next step",
      icon: AlarmClockOff,
      to: "/leads?view=table&status=open&noNextStep=true",
      group: "actions",
      permission: "leads:read",
      keywords: ["follow up", "forgotten", "cold", "chase"],
      description: "The ones nobody has scheduled anything for",
    },
    {
      key: "leads.stale",
      label: "Stale leads",
      icon: TriangleAlert,
      to: "/leads?view=table&status=open&stale=true",
      group: "actions",
      permission: "leads:read",
      keywords: ["stuck", "old", "aging", "quiet"],
    },
    {
      key: "leads.reports",
      label: "Pipeline reports",
      icon: BarChart3,
      to: "/leads/reports",
      group: "navigate",
      permission: ["leads:read", "reports:view"],
      keywords: ["funnel", "win rate", "forecast", "conversion", "speed to lead"],
    },
  ],
  settingsSections: [
    {
      key: "pipelines",
      label: "Pipelines & stages",
      path: "pipelines",
      group: "Sales",
      order: 200,
      icon: GitBranch,
      permission: "leads:read",
      description: "Stages, probabilities, stale thresholds, sources and lost reasons",
      element: (
        <Suspense fallback={<SettingsFallback />}>
          <PipelineSettingsPage />
        </Suspense>
      ),
    },
  ],
});
